
import { useEffect } from 'react';
import { 
  SparcConfig,
  initializeSparcMCP,
  getAvailableApiProviders
} from '@/services/agentic/sparcService';

/**
 * Hook for handling SPARC agent initialization
 */
export const useSparcInitialization = (
  config: SparcConfig,
  autoInitialize: boolean,
  state: {
    setLoading: (loading: boolean) => void;
    setError: (error: Error | null) => void;
    setInitialized: (initialized: boolean) => void;
    setConfig: (config: SparcConfig) => void;
    setAvailableProviders: (providers: any[]) => void;
  }
) => {
  const { setLoading, setError, setInitialized, setConfig, setAvailableProviders } = state;
  
  // Initialize on mount if autoInitialize is true
  useEffect(() => {
    if (autoInitialize) {
      initAgent();
    }
  }, [autoInitialize]);
  
  // Initialize the agent and get available API providers
  const initAgent = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const initResult = await initializeSparcMCP(config);
      
      if (initResult.success) {
        setInitialized(true);
        
        // If providers are available, update the config
        if (initResult.availableProviders && initResult.availableProviders.length > 0) {
          const defaultProvider = initResult.defaultProvider || initResult.availableProviders[0];
          
          setConfig({
            ...config,
            apiProvider: defaultProvider as 'openai' | 'anthropic'
          });
          
          // Get detailed provider info
          const providers = await getAvailableApiProviders();
          setAvailableProviders(providers);
        }
      } else {
        setError(new Error("SPARC initialization failed"));
      }
      
      return initResult.success;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error during initialization');
      setError(error);
      setInitialized(false);
      return false;
    } finally {
      setLoading(false);
    }
  };

  return {
    initAgent
  };
};
